/**
 * role-context.tsx — Sprint 24.4
 *
 * Provides the current admin's role to the component tree.
 *
 * On mount, attempts GET /admin/me and caches the returned role in
 * localStorage (via setCachedRole). Falls back to the cached value
 * immediately so UI is never blank on refresh.
 *
 * Usage:
 *   const { role, loading, isSuperAdmin } = useRole();
 *   if (!isSuperAdmin) { ... hide ban button ... }
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getMe, getCachedRole } from '../api/admin-client';

type RoleContextValue = {
  role: string;
  loading: boolean;
  /** true when role === 'super_admin' */
  isSuperAdmin: boolean;
  /** true when role is any admin tier (moderator, admin, super_admin) */
  isAdmin: boolean;
  /** Trigger a manual refresh of the role from the server */
  refresh: () => void;
};

const RoleContext = createContext<RoleContextValue>({
  role: '',
  loading: true,
  isSuperAdmin: false,
  isAdmin: false,
  refresh: () => {},
});

export function RoleProvider({ children }: { children: ReactNode }) {
  // Seed from localStorage cache so the initial render isn't blank
  const [role, setRole] = useState<string>(getCachedRole);
  const [loading, setLoading] = useState(true);

  async function fetchRole() {
    setLoading(true);
    try {
      const me = await getMe();
      setRole(me.admin_role ?? '');
    } catch {
      // If the endpoint isn't live yet (404/501), fall back to the cached value.
      // A 401/403 is handled inside adminFetch (redirects to /login).
      setRole(getCachedRole());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRole();
  }, []);

  const value: RoleContextValue = {
    role,
    loading,
    isSuperAdmin: role === 'super_admin',
    isAdmin: role === 'super_admin' || role === 'admin' || role === 'moderator',
    refresh: fetchRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole(): RoleContextValue {
  return useContext(RoleContext);
}
